import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/shared/ui/input';

export type CurrencyCode = 'BRL' | 'USD' | 'EUR';

interface CurrencyInputProps {
  value?: number;
  onValueChange: (value: number | undefined) => void;
  currency?: CurrencyCode;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

function formatCurrency(value: number, currency: CurrencyCode): string {
  const locale = 'pt-BR';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function CurrencyInput({
  value,
  onValueChange,
  currency = 'BRL',
  placeholder,
  disabled,
  id,
  name,
  className,
}: CurrencyInputProps) {
  const [cents, setCents] = useState<string>('');
  const formatter = useMemo(() => (n: number) => formatCurrency(n, currency), [currency]);

  // Sync from external value
  useEffect(() => {
    if (value === undefined || isNaN(value)) {
      setCents('');
      return;
    }
    const asCents = Math.round(value * 100);
    setCents(String(Math.max(0, asCents)));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/\D/g, '');
    setCents(onlyDigits);
    if (!onlyDigits) {
      onValueChange(undefined);
      return;
    }
    const num = Number(onlyDigits) / 100;
    onValueChange(num);
  };

  // Compute display from cents
  const display = useMemo(() => {
    if (!cents) return '';
    const num = Number(cents) / 100;
    return formatter(num);
  }, [cents, formatter]);

  return (
    <Input
      id={id}
      name={name}
      value={display}
      onChange={handleChange}
      inputMode="numeric"
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}
